<!DOCTYPE html>
<template>
  <div class="app-container">
    <div class="page-loader" v-if="loading">
      <div class="loader-container">
        <div class="loader-icon">
          <i class="el-icon-data-analysis"></i>
        </div>
        <div class="loader-text">GO Count Object</div>
        <div class="loader-spinner">
          <div class="spinner-dot"></div>
          <div class="spinner-dot"></div>
          <div class="spinner-dot"></div>
        </div>
      </div>
    </div>

    <header class="header" :class="{ 'header-scrolled': isScrolled }">
      <div class="nav-container">
        <div class="logo">
          <i class="el-icon-data-analysis logo-icon"></i>
          <div class="logo-text">GO Count Object -Thing Counter</div>
        </div>
      </div>
    </header>

    <main class="main-content">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <footer class="footer">
      <div class="footer-container">
        <div class="footer-main">
          <div class="footer-brand">
            <div class="footer-logo">
              <i class="el-icon-data-analysis logo-icon"></i>
              <span class="logo-text">GO Count Object</span>
            </div>
            <div class="footer-content">
              <p class="footer-slogan">Smart Counting · Easy Management</p>
              <p class="footer-description">GO Count Object is a professional intelligent counting tool dedicated to providing users with accurate and efficient counting solutions, enabling intelligent data management and analysis.</p>
            </div>
          </div>
          <div class="footer-quick-links">
            <div class="footer-quick-links-title">Quick Links</div>
            <a href="#features" class="footer-link">Features</a>
            <a href="#" class="footer-link">Download App</a>
            <a href="#" class="footer-link">Contact Us</a>
          </div>
        </div>
        
        <div class="footer-bottom">
          <div class="footer-copyright">
            <span>© 2024 GO Count Object. All Rights Reserved</span>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      isScrolled: false,
      loading: true
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    
    // Simulate page loading process, hide loading animation after 1.5 seconds
    setTimeout(() => {
      this.loading = false;
    }, 1500);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    }
  }
}
</script>

<style>
:root {
  --color-primary: #409eff;
  --color-primary-dark: #337ecc;
  --color-secondary: #7a5af8;
  --color-accent: #ff9f43;
  --color-success: #28c76f;
  --color-text-primary: #303133;
  --color-text-secondary: #606266;
  --color-text-muted: #909399;
  --color-border: rgba(0, 0, 0, 0.1);
  --color-background: #f9fafc;
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.1);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.1);
  --border-radius-sm: 4px;
  --border-radius-md: 8px;
  --border-radius-lg: 16px;
  --transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  --container-width: 1200px;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html {
  height: 100%;
  overflow-x: hidden;
  width: 100%;
}

body {
  margin: 0;
  padding: 0;
  font-family: 'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: var(--color-text-secondary);
  line-height: 1.6;
  background: linear-gradient(135deg, #f0f4fc 0%, #e6eeff 50%, #f0f4fc 100%);
  height: 100%;
  width: 100%;
  overflow-x: hidden;
  position: relative;
}

body::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    radial-gradient(circle at 10% 10%, rgba(120, 200, 255, 0.1) 0%, transparent 40%),
    radial-gradient(circle at 90% 20%, rgba(122, 90, 248, 0.12) 0%, transparent 45%),
    radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.9) 0%, transparent 40%),
    radial-gradient(circle at 80% 80%, rgba(120, 200, 255, 0.08) 0%, transparent 35%);
  z-index: -1;
}

body::after {
  content: '';
  position: fixed;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="none"/><rect width="1" height="1" fill="rgba(122, 90, 248, 0.03)" x="0" y="0"/></svg>');
  animation: subtle-drift 120s linear infinite;
  opacity: 0.3;
  z-index: -2;
}

@keyframes subtle-drift {
  0% {
    transform: translate(0, 0) rotate(0deg);
  }
  100% {
    transform: translate(-20px, -20px) rotate(1deg);
  }
}

h1, h2, h3, h4, h5, h6 {
  color: var(--color-text-primary);
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 1rem;
}

h1 {
  font-size: 2.5rem;
  letter-spacing: -0.02em;
}

h2 {
  font-size: 2rem;
  letter-spacing: -0.01em;
}

h3 {
  font-size: 1.5rem;
}

p {
  margin-bottom: 1.2rem;
}

a {
  color: var(--color-primary);
  text-decoration: none;
  transition: var(--transition);
}

a:hover {
  color: var(--color-secondary);
}

button {
  cursor: pointer;
  font-family: inherit;
}

ul, ol {
  padding-left: 1.5rem;
}

#app {
  height: 100%;
  width: 100%;
  overflow-x: hidden;
  position: relative;
}

.app-container {
  min-height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  background: linear-gradient(135deg, #f0f4fc 0%, #e6eeff 50%, #f0f4fc 100%);
}

.main-content {
  flex: 1 0 auto;
  width: 100%;
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 0;
  position: relative;
}

/* Header & Navigation */
.header {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
}

.header-scrolled {
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.nav-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 1.5rem;
  max-width: var(--container-width);
  margin: 0 auto;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--border-radius-md);
  transition: all 0.3s ease;
}

.logo:hover {
  background: rgba(255, 255, 255, 0.8);
  transform: translateY(-2px);
  box-shadow: var(--shadow-sm);
}

.logo-icon {
  font-size: 2rem;
  color: var(--color-primary);
  transition: transform 0.3s ease;
}

.logo:hover .logo-icon {
  transform: rotate(15deg);
}

.logo-text {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  text-fill-color: transparent;
  white-space: nowrap;
}

.main-nav {
  display: flex;
  gap: 2rem;
  align-items: center;
  position: relative;
}

.nav-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  position: relative;
  padding: 0.5rem 0;
  transition: color 0.3s ease;
}

.nav-link:hover, .nav-link.active {
  color: var(--color-primary);
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
  transition: width 0.3s ease;
}

.nav-link:hover::after, .nav-link.active::after {
  width: 100%;
}

.nav-indicator {
  position: absolute;
  bottom: -2px;
  height: 2px;
  background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
  transition: all 0.3s ease;
  opacity: 0;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.btn-language {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.8);
  color: var(--color-text-secondary);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-language:hover {
  background: rgba(64, 158, 255, 0.1);
  border-color: rgba(64, 158, 255, 0.3);
  transform: translateY(-2px);
}

/* Footer */
.footer {
  flex-shrink: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.98));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.8);
  padding: 3rem 0 0;
  margin: 0;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.03);
  position: relative;
  width: 100%;
}

.footer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, 
    rgba(122, 90, 248, 0.2),
    rgba(64, 158, 255, 0.2),
    rgba(122, 90, 248, 0.2)
  );
}

.footer-container {
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 0 3rem;
  position: relative;
  z-index: 1;
}

.footer-main {
  display: flex;
  justify-content: space-between;
  margin-bottom: 3rem;
  gap: 6rem;
}

.footer-brand {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 480px;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.footer-logo .logo-icon {
  font-size: 2.2rem;
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.footer-logo .logo-text {
  font-size: 1.6rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.footer-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.footer-slogan {
  font-size: 1.25rem;
  color: var(--color-text-primary);
  line-height: 1.4;
  margin: 0;
  font-weight: 600;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, var(--color-text-primary), var(--color-text-secondary));
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.footer-description {
  color: var(--color-text-secondary);
  font-size: 1rem;
  line-height: 1.7;
  margin: 0;
  opacity: 0.85;
  font-weight: 400;
}

.footer-quick-links {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-top: 1rem;
}

.footer-quick-links-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.75rem;
  letter-spacing: -0.01em;
  position: relative;
}

.footer-quick-links-title::after {
  content: '';
  position: absolute;
  bottom: -0.5rem;
  left: 0;
  width: 2rem;
  height: 2px;
  background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
  border-radius: 1px;
}

.footer-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  opacity: 0.85;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0;
}

.footer-link::before {
  content: '';
  width: 0;
  height: 1.5px;
  background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
  position: absolute;
  bottom: 0;
  left: 0;
  transition: width 0.3s ease;
  opacity: 0;
}

.footer-link:hover {
  color: var(--color-primary);
  transform: translateX(5px);
  opacity: 1;
}

.footer-link:hover::before {
  width: 100%;
  opacity: 1;
}

.footer-bottom {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  position: relative;
  margin-top: -1rem;
}

.footer-bottom::before {
  content: '';
  position: absolute;
  top: -1px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 1px;
  background: linear-gradient(90deg,
    transparent,
    rgba(122, 90, 248, 0.2),
    rgba(64, 158, 255, 0.2),
    rgba(122, 90, 248, 0.2),
    transparent
  );
}

.footer-copyright {
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  opacity: 0.8;
  font-weight: 500;
}

@media (max-width: 768px) {
  .footer {
    padding: 1.5rem 0 0;
  }
  
  .footer-container {
    padding: 0 1.25rem;
  }
  
  .footer-main {
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  .footer-brand {
    max-width: 100%;
    align-items: center;
    text-align: center;
    gap: 0.75rem;
  }
  
  .footer-logo {
    justify-content: center;
    margin-bottom: 0.25rem;
  }
  
  .footer-logo .logo-icon {
    font-size: 1.75rem;
  }
  
  .footer-logo .logo-text {
    font-size: 1.25rem;
  }
  
  .footer-content {
    gap: 0.75rem;
  }
  
  .footer-slogan {
    font-size: 1rem;
    margin: 0;
  }
  
  .footer-description {
    font-size: 0.9rem;
    line-height: 1.5;
    padding: 0 1rem;
    opacity: 0.85;
  }
  
  .footer-quick-links {
    align-items: center;
    text-align: center;
    gap: 0.5rem;
    padding-top: 0;
  }
  
  .footer-quick-links-title {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }
  
  .footer-link {
    font-size: 0.9rem;
    padding: 0.4rem 0;
  }
  
  .footer-bottom {
    padding: 1rem 0;
    margin-top: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
  
  .footer-copyright {
    font-size: 0.85rem;
    opacity: 0.8;
  }
}

@media (max-width: 480px) {
  .footer {
    padding: 1.25rem 0 0;
  }
  
  .footer-container {
    padding: 0 1rem;
  }
  
  .footer-main {
    gap: 1.25rem;
    margin-bottom: 1.25rem;
  }
  
  .footer-logo .logo-icon {
    font-size: 1.5rem;
  }
  
  .footer-logo .logo-text {
    font-size: 1.1rem;
  }
  
  .footer-slogan {
    font-size: 0.95rem;
  }
  
  .footer-description {
    font-size: 0.85rem;
    padding: 0 0.75rem;
  }
  
  .footer-quick-links-title {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
  
  .footer-link {
    font-size: 0.85rem;
    padding: 0.35rem 0;
  }
  
  .footer-bottom {
    padding: 0.75rem 0;
  }
  
  .footer-copyright {
    font-size: 0.8rem;
    opacity: 0.75;
  }
}

/* Page transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 加载动画样式 */
.page-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f0f4fc 0%, #e6eeff 50%, #f0f4fc 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  transition: opacity 0.5s, visibility 0.5s;
}

.loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.loader-icon {
  font-size: 5rem;
  color: var(--color-primary);
  animation: pulse 1.5s ease-in-out infinite;
}

.loader-text {
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  text-fill-color: transparent;
}

.loader-spinner {
  display: flex;
  gap: 0.5rem;
}

.spinner-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--color-primary);
  animation: bounce 1.4s infinite ease-in-out both;
}

.spinner-dot:nth-child(1) {
  animation-delay: -0.32s;
  background: var(--color-primary);
}

.spinner-dot:nth-child(2) {
  animation-delay: -0.16s;
  background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
}

.spinner-dot:nth-child(3) {
  background: var(--color-secondary);
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.7;
  }
}

/* Responsive design */
@media (max-width: 768px) {
  html, body, #app {
    height: 100%;
    width: 100%;
    overflow-x: hidden;
    position: relative;
  }
  
  .app-container {
    min-height: 100%;
    width: 100%;
    overflow-x: hidden;
  }
  
  .main-content {
    flex: 1 0 auto;
    width: 100%;
    padding: 0;
  }
  
  .nav-container {
    padding: 0.8rem 1rem;
  }
  
  .logo-text {
    font-size: 1.2rem;
  }
  
  .footer {
    padding: 0.3rem 0 0;
    margin: 0;
  }
  
  .footer-container {
    padding: 0 0.5rem;
  }
  
  .footer-main {
    margin-bottom: 0.1rem;
  }
  
  .footer-brand {
    gap: 0;
  }
  
  .footer-slogan {
    font-size: 0.85rem;
    margin: 0.1rem 0;
  }
  
  .footer-brand p {
    font-size: 0.75rem;
    margin: 0.1rem 0;
    padding: 0 0.5rem;
  }
  
  .footer-bottom {
    padding: 0.5rem 0;
    margin-top: 0.5rem;
  }
  
  .footer-copyright {
    font-size: 0.7rem;
    padding: 0.5rem 0;
  }
}

@media (max-width: 480px) {
  .logo-icon {
    font-size: 1.5rem;
  }
  
  .logo-text {
    font-size: 0.9rem;
  }
  
  .footer-slogan {
    font-size: 0.8rem;
  }
  
  .footer-brand p {
    font-size: 0.7rem;
    line-height: 1.3;
  }
  
  .footer-bottom {
    padding: 0.5rem 0;
    margin-top: 0.75rem;
  }
  
  .footer-copyright {
    font-size: 0.65rem;
    padding: 0.5rem 0;
  }
}

.feature-description {
  color: var(--color-text-secondary);
  font-size: 1rem;
  line-height: 1.7;
  margin: 0;
  opacity: 0.85;
  font-weight: 400;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

@media (max-width: 768px) {
  .feature-description {
    font-size: 0.9rem;
    line-height: 1.5;
    padding: 0 1rem;
  }

  .feature-section {
    padding: 1.5rem;
    margin: 0 1rem;
    width: calc(100% - 2rem);
    overflow: hidden;
  }

  .feature-content {
    width: 100%;
    max-width: 100%;
    padding: 0;
  }

  .feature-title {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
    line-height: 1.4;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }
}

@media (max-width: 480px) {
  .feature-description {
    font-size: 0.85rem;
    padding: 0 0.5rem;
    line-height: 1.4;
  }

  .feature-section {
    padding: 1rem;
    margin: 0 0.5rem;
  }

  .feature-title {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }

  .feature-content {
    padding: 0;
  }
}
</style>

